import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { getRawEntity, type RawEntityResponse, saveRawEntity } from "@/api/admin-api"

import { GC_TIME } from "./_shared"

// =============================================================================
// Generic raw-entity hooks.
// =============================================================================
//
// One pair of hooks per admin Raw-JSON-tab integration. Callers pass the
// raw endpoint path (e.g. ``/admin/routes/<id>/raw``) plus the React Query
// keys to invalidate on successful save.
//
// Why a generic pair rather than per-entity? Each new entity's raw flow
// is structurally identical — fetch {entity, hash}, save {entity,
// expected_hash, reason} — only the endpoint and invalidation keys
// change. The alternative (7+ near-identical hook pairs) is pure
// copy-paste with no additional safety.

export function useRawEntityQuery<TEntity = Record<string, unknown>>(
  endpoint: string | null,
  enabled = true
) {
  return useQuery<RawEntityResponse<TEntity>>({
    queryKey: ["admin", "raw-entity", endpoint],
    queryFn: () => getRawEntity<TEntity>(endpoint as string),
    enabled: enabled && !!endpoint,
    // Always refetch on mount: the {entity, hash} pair drives optimistic
    // concurrency. A cached hash that has gone stale would let the dialog
    // open against an outdated baseline and only surface the conflict at
    // save time as a 409.
    staleTime: 0,
    gcTime: GC_TIME,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  })
}

export type SaveRawEntityArgs = {
  entity: Record<string, unknown>
  expectedHash: string
  reason: string
  source?: string
}

export function useSaveRawEntityMutation<TEntity = Record<string, unknown>>(args: {
  endpoint: string
  /** Query keys to invalidate after a successful save. The raw entity's
   *  own query is always invalidated automatically; this option lets the
   *  caller also refresh sibling queries (e.g. the entity's list view). */
  invalidateOnSuccess?: readonly unknown[][]
}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (mutation: SaveRawEntityArgs) =>
      saveRawEntity<TEntity>({
        endpoint: args.endpoint,
        entity: mutation.entity,
        expectedHash: mutation.expectedHash,
        reason: mutation.reason,
        source: mutation.source,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "raw-entity", args.endpoint] })
      for (const key of args.invalidateOnSuccess ?? []) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })
}
